import { EV } from '../core/eventbus.js';
import { Repo } from '../data/schema.js';
import { localUid } from '../net/account.js';
import { normalizeUrl } from './tv.js';

/**
 * 白板 · 灵感收集器：屋里电视右手边立着的一块板。
 *
 * 用法：走到板子前按 E —— 想写什么就写什么，一条记一个念头（有标题、正文、标签），
 * 自动记下起笔的时刻、是哪位写的。板子分几栏（分类），栏是 TAB，随便加；「网址」那栏里
 * 每条右边有「投电视」「投牌桌」，按一下屋里那台电视（或那张游戏桌）就换到这个台，
 * 房间里的人一起换。
 * 板上写下就留痕：别人的念头看得见，揭不下来——各人的灵感只有本人能删。
 *
 * 屋里的人是**共用一块板**的：写一条、改一条、揭一条都顺着联机那根线同步出去，
 * 谁进屋都会先问一圈，把板上现有的全搬过来；留一份整块板的快照在网上，
 * 屋里暂时没人的时候后来者也能看见先前写的。
 *
 * 存在哪儿：先落在白板这件器物自己的状态里（和电视记频道一个道理），跟着存档走；
 * 人走到别处（那张图里根本没这块板）时先在本机暂存一份，回屋再并回板上。
 * 不上传服务器、不进书库。列表上只写标题，点开才是正文。
 */

/** 出厂就有的两栏：灵感随手记，网址专门用来投电视 */
export const BASE_CATS = ['灵感', '网址'];
/** 这一栏里的每条都当作网址，右边带「投电视」 */
export const URL_CAT = '网址';

export function fmtTime(ms) {
  const d = new Date(ms || Date.now());
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 列表里挤得慌，就写「几月几日 几点几分」 */
export function fmtShort(ms) {
  const d = new Date(ms || Date.now());
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 标签栏里写什么都行：逗号、空格、顿号都算分隔 */
export function parseTags(raw) {
  const out = [];
  for (const t of String(raw || '').split(/[,，、;；\s]+/)) {
    const s = t.trim();
    if (s && !out.includes(s)) out.push(s);
    if (out.length >= 8) break;
  }
  return out;
}

function newId() {
  return 'n' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

/** 屋里共享内容的本机备份：人在别处（那张图里没这块板）时先暂存在这儿，回屋再并回板上 */
const LOCAL_KEY = 'dtj_board';
/** 板上最多钉多少条：多了先把最旧的抖掉，免得同步的那包越滚越大 */
const MAX_NOTES = 400;
/** 隔多久才把整块板的快照往网上留一份（留给后来的人接着看） */
const SNAP_GAP = 10000;

export class WhiteboardPanel {
  /**
   * @param {import('../core/engine.js').Engine} engine
   * @param {{tv?: import('./tv.js').TVPanel, table?: import('./tv.js').TVPanel, net?: import('../net/net.js').NetProxy, el?: HTMLElement}} [opts]
   */
  constructor(engine, opts = {}) {
    this.engine = engine;
    this.tv = opts.tv || this.engine.tv || null;          // 电视面板
    this.table = opts.table || this.engine.table || null; // 游戏桌面板；网址那栏也能投到这里
    this.net = opts.net || this.engine.net || null;       // 联机那条线：板上的人共用一块板
    this.el = opts.el || document.getElementById('whiteboard');
    this.prop = null;
    this.state = { cats: [...BASE_CATS], tab: BASE_CATS[0], notes: [], dead: [] };
    this.draft = null;                     // 正在编辑的那条（新建的也算）
    this.filterTag = '';
    this.search = '';
    this._snapTimer = null;                // 留快照用的延时器
    this._lastSnap = 0;
    this._askTimer = null;                 // 回新人的延时器（谁先发到就算谁的）
    this._lastTip = 0;

    // 先把本机那份摆进来：人还没走到板子跟前，也能答得出「板上现在有什么」
    this.absorb(this.loadLocal());
    if (this.net) this.net.on((msg) => this.onNet(msg));

    if (!this.el) return;                  // 没有 DOM（冒烟测试）就安静退场
    this.bind();
  }

  q(sel) { return this.el ? this.el.querySelector(sel) : null; }

  isOpen() { return !!this.el && !this.el.classList.contains('hidden'); }

  toast(text, kind = 'info') { this.engine.events.emit(EV.TOAST, { text, kind }); }

  bind() {
    const closeBtn = this.el.querySelector('[data-close]');
    if (closeBtn) closeBtn.onclick = () => this.close();

    const b = (name) => this.q(`[data-wb="${name}"]`);
    if (b('new')) b('new').onclick = () => this.newNote();
    if (b('addcat')) b('addcat').onclick = () => this.askNewCat();
    if (b('delcat')) b('delcat').onclick = () => this.removeCat();
    if (b('save')) b('save').onclick = () => this.saveAndBack();
    if (b('del')) b('del').onclick = () => this.removeNote();
    if (b('back')) b('back').onclick = () => this.back();
    if (b('totv')) b('totv').onclick = () => this.cast(this.draft, 'tv');
    if (b('totable')) b('totable').onclick = () => this.cast(this.draft, 'gametable');

    const search = b('search');
    if (search) search.oninput = () => { this.search = search.value; this.renderList(); };

    // 标题 / 标签里回车就存下；正文要换行，所以用 Ctrl+回车
    const title = b('title'), tag = b('tag'), body = b('body');
    if (title) title.onkeydown = (e) => { if (e.key === 'Enter') this.saveAndBack(); };
    if (tag) tag.onkeydown = (e) => { if (e.key === 'Enter') this.saveAndBack(); };
    if (body) body.onkeydown = (e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) this.saveAndBack(); };

    this.engine.events.on(EV.NOTES_OPEN, ({ prop }) => this.open(prop));
  }

  /* ------------------------------ 开合 ------------------------------ */

  open(prop) {
    if (!this.el) return;
    if (prop && prop.type && prop.type !== 'whiteboard') return;   // 只认这块板
    this.prop = prop || null;
    this.absorb(this.prop && this.prop.state);   // 板上钉着的（跟着存档走）
    this.absorb(this.loadLocal());               // 人在别处时暂存在本机的
    // 出厂两栏得一直在（老存档或手改过的补回来）
    for (const c of BASE_CATS) if (!this.state.cats.includes(c)) this.state.cats.push(c);
    if (!this.state.cats.includes(this.state.tab)) this.state.tab = this.state.cats[0];
    this.filterTag = '';
    this.search = '';
    const s = this.q('[data-wb="search"]');
    if (s) s.value = '';
    this.draft = null;
    this.el.classList.remove('hidden');
    this.showList();
    this.askAll();                               // 屋里的人照着最新的那份替我对一对
  }

  close() {
    if (!this.el) return;
    const note = this.draft;
    this.saveDraft();          // 关板子不丢字
    // 写了一半就合上板的，也替他发出去（嫌乱可以查看完再删）
    if (note && this.state.notes.some((n) => n.id === note.id)) this.shareNote(note);
    this.el.classList.add('hidden');
    this.draft = null;
  }

  /* --------------------------- 板上 / 编辑 --------------------------- */

  showList() {
    const view = this.q('[data-wb="view"]'), edit = this.q('[data-wb="edit"]');
    if (view) view.classList.remove('hidden');
    if (edit) edit.classList.add('hidden');
    this.renderTabs();
    this.renderTags();
    this.renderList();
  }

  showEdit() {
    const view = this.q('[data-wb="view"]'), edit = this.q('[data-wb="edit"]');
    if (view) view.classList.add('hidden');
    if (edit) edit.classList.remove('hidden');
  }

  /* ------------------------------ 分类栏 ------------------------------ */

  renderTabs() {
    const box = this.q('[data-wb="tabs"]');
    if (!box) return;
    box.innerHTML = '';
    for (const cat of this.state.cats) {
      const btn = document.createElement('button');
      btn.className = 'wb-tab' + (cat === this.state.tab ? ' on' : '');
      btn.textContent = cat;
      const n = this.state.notes.filter((x) => x.cat === cat).length;
      if (n) btn.textContent = `${cat} ${n}`;
      btn.onclick = () => { this.state.tab = cat; this.filterTag = ''; this.save(); this.renderTabs(); this.renderTags(); this.renderList(); };
      box.appendChild(btn);
    }
  }

  /** 新分类：就在 TAB 那一行里就地输入，回车定下 */
  askNewCat() {
    const box = this.q('[data-wb="tabs"]');
    if (!box || box.querySelector('input')) return;
    const inp = document.createElement('input');
    inp.className = 'wb-search';
    inp.placeholder = '分类名，回车定下';
    let ended = false;
    const done = (ok) => {
      if (ended) return;        // 回车与失焦会连着来，只认第一下
      ended = true;
      const v = inp.value.trim();
      inp.remove();
      if (!ok || !v) { this.renderTabs(); return; }
      if (this.state.cats.includes(v)) return this.toast('已经有这一栏了。', 'warn');
      this.state.cats.push(v);
      this.state.tab = v;
      this.save();
      this.shareAll(true);       // 栏变了：整块板发一份，大家的分栏才对得上
      this.toast(`多了一栏「${v}」，屋里的人也跟着多一栏。`, 'good');
      this.renderTabs(); this.renderTags(); this.renderList();
    };
    inp.onkeydown = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); done(true); }
      else if (e.key === 'Escape') { e.preventDefault(); done(false); }
    };
    inp.onblur = () => done(true);
    box.appendChild(inp);
    inp.focus();
  }

  /** 删掉当前这一栏：出厂的两栏不许删，栏里的东西挪回「灵感」 */
  removeCat() {
    const cat = this.state.tab;
    if (BASE_CATS.includes(cat)) return this.toast(`「${cat}」这块板自带，删不掉。`, 'warn');
    const inIt = this.state.notes.filter((n) => n.cat === cat).length;
    const msg = inIt ? `删掉「${cat}」？里面的 ${inIt} 条会挪到「${BASE_CATS[0]}」。` : `删掉「${cat}」？`;
    if (typeof window !== 'undefined' && window.confirm && !window.confirm(msg)) return;
    for (const n of this.state.notes) if (n.cat === cat) n.cat = BASE_CATS[0];
    this.state.cats = this.state.cats.filter((c) => c !== cat);
    this.state.tab = BASE_CATS[0];
    this.filterTag = '';
    this.save();
    this.shareAll(true);
    this.showList();
    this.toast(`「${cat}」撤了，屋里的人那儿也一并撤下。`, 'info');
  }

  /* ------------------------------ 标签 ------------------------------ */

  renderTags() {
    const box = this.q('[data-wb="tags"]');
    if (!box) return;
    box.innerHTML = '';
    const set = new Set();
    for (const n of this.state.notes) {
      if (n.cat !== this.state.tab) continue;
      for (const t of n.tags || []) set.add(t);
    }
    if (!set.size) return;
    const chip = (label, on, fn) => {
      const b = document.createElement('button');
      b.className = 'wb-chip' + (on ? ' on' : '');
      b.textContent = label;
      b.onclick = fn;
      box.appendChild(b);
    };
    chip('全部', !this.filterTag, () => { this.filterTag = ''; this.renderTags(); this.renderList(); });
    for (const t of set) {
      chip(t, this.filterTag === t, () => {
        this.filterTag = this.filterTag === t ? '' : t;
        this.renderTags(); this.renderList();
      });
    }
  }

  /* ------------------------------ 列表 ------------------------------ */

  renderList() {
    const list = this.q('[data-wb="list"]');
    if (!list) return;
    list.innerHTML = '';
    const kw = this.search.trim().toLowerCase();
    let rows = this.state.notes.filter((n) => n.cat === this.state.tab);
    if (this.filterTag) rows = rows.filter((n) => (n.tags || []).includes(this.filterTag));
    if (kw) {
      rows = rows.filter((n) => ((n.title || '') + (n.body || '') + (n.tags || []).join(' ')).toLowerCase().includes(kw));
    }
    rows.sort((a, b) => (b.at || 0) - (a.at || 0));

    if (!rows.length) {
      const p = document.createElement('div');
      p.className = 'wb-empty';
      p.textContent = this.state.notes.length ? '这一栏还没有（别的栏里倒是有）' : '板上还空着，点「新记一条」起个头。';
      list.appendChild(p);
      return;
    }

    const isUrl = this.state.tab === URL_CAT;
    for (const note of rows) {
      const card = document.createElement('div');
      card.className = 'wb-card';
      const b = document.createElement('b');
      b.textContent = note.title || '（没写标题）';
      card.appendChild(b);
      // 起笔时刻与是谁写的，钉在标题右边一行小字
      const meta = document.createElement('span');
      meta.className = 'wb-meta';
      meta.textContent = `${fmtShort(note.at)} · ${note.by || '未署名'}`;
      card.appendChild(meta);
      if (isUrl) {
        const mkCast = (label, kind) => {
          const btn = document.createElement('button');
          btn.className = 'btn';
          btn.textContent = label;
          btn.onclick = (e) => { e.stopPropagation(); this.cast(note, kind); };
          return btn;
        };
        card.appendChild(mkCast('投电视', 'tv'));
        card.appendChild(mkCast('投牌桌', 'gametable'));
      }
      card.onclick = () => this.editNote(note.id);
      list.appendChild(card);
    }
  }

  /* --------------------------- 谁写的 / 谁能揭 --------------------------- */

  /** 我这人的号：登录了是服务器给的 uid，没登录是本机的号（跟着 localStorage 走） */
  myUid() {
    const a = this.engine.account;
    const id = a && a.identity ? a.identity.uid : '';
    return id || (this.engine.net ? this.engine.net.uid : '') || '';
  }

  /** 这台机器的号：登录、登出都不换，用它把「登录前写的一条」还能认回来 */
  myHost() {
    try { return localUid(); } catch { return ''; }
  }

  /** 我在屋里的称呼：注册名优先，没有就用串门面板里填的那个 */
  meName() {
    const a = this.engine.account;
    if (a && a.verified && a.name) return a.name;
    const me = this.engine.me;
    if (me && me.name) return me.name;
    try {
      const p = JSON.parse(localStorage.getItem('dtj_net') || '{}');
      if (p && p.name) return p.name;
    } catch { /* 没有存过的就算了 */ }
    return '';
  }

  /**
   * 这一条是不是我写的。板上老的条目没记身份（既没 uid 也没 host），照老规矩还是谁都能动。
   * 先看账号，再看是不是同一台机器写的；同机器时名字对不上就不算数——
   * 免得几个人共用一台电脑，把别人的灵感给揭了。
   */
  canRemove(note) {
    if (!note) return true;
    if (!note.uid && !note.host) return true;
    const mine = this.myUid();
    if (mine && note.uid === mine) return true;
    const host = this.myHost();
    if (host && note.host === host) {
      const myName = this.meName();
      // 谁都没留名字的我，认不出也认得回自己的；留了名字的，名字得对上
      if (!note.by || !myName || note.by === myName) return true;
    }
    const myName = this.meName();
    return !!myName && !!note.by && note.by === myName;
  }

  /* ------------------------------ 写一条 ------------------------------ */

  newNote() {
    const note = {
      id: newId(), cat: this.state.tab, title: '', body: '', tags: [],
      at: Date.now(), by: this.meName(), uid: this.myUid(), host: this.myHost(), _new: true,
    };
    this.state.notes.push(note);
    this.editNote(note.id);
  }

  editNote(id) {
    const note = this.state.notes.find((n) => n.id === id);
    if (!note) return;
    this.draft = note;
    const title = this.q('[data-wb="title"]'), body = this.q('[data-wb="body"]'),
      tag = this.q('[data-wb="tag"]'), cat = this.q('[data-wb="cat"]'), time = this.q('[data-wb="time"]');
    if (title) title.value = note.title || '';
    if (body) body.value = note.body || '';
    if (tag) tag.value = (note.tags || []).join(' ');
    if (cat) {
      cat.innerHTML = '';
      for (const c of this.state.cats) {
        const o = document.createElement('option');
        o.value = c; o.textContent = c;
        if (c === note.cat) o.selected = true;
        cat.appendChild(o);
      }
      cat.onchange = () => {
        const tv = this.q('[data-wb="totv"]');
        if (tv) tv.classList.toggle('hidden', cat.value !== URL_CAT);
      };
    }
    if (time) {
      const day = Repo.getWorld(this.engine.db).day;
      const who = note.by ? `　${note.by} 记` : '';
      time.textContent = `起笔于 ${fmtTime(note.at)}　第 ${day} 日${who}`;
    }
    // 「网址」那栏才露出投放的两个按钮（电视 / 牌桌）
    const isUrlNow = note.cat === URL_CAT;
    for (const sel of ['[data-wb="totv"]', '[data-wb="totable"]']) {
      const btn = this.q(sel);
      if (btn) btn.classList.toggle('hidden', !isUrlNow);
    }
    // 别人写的：看得见，改不动也揭不下来
    const mine = this.canRemove(note);
    const delBtn = this.q('[data-wb="del"]');
    if (delBtn) delBtn.classList.toggle('hidden', !mine);
    for (const sel of ['[data-wb="title"]', '[data-wb="body"]', '[data-wb="tag"]']) {
      const el = this.q(sel);
      if (el) el.readOnly = !mine;
    }
    if (cat) cat.disabled = !mine;
    this.showEdit();
    if (title && mine) title.focus();
  }

  /** 把表单里写的收进这一条（不关编辑页） */
  saveDraft() {
    const note = this.draft;
    const title = this.q('[data-wb="title"]'), body = this.q('[data-wb="body"]'),
      tag = this.q('[data-wb="tag"]'), cat = this.q('[data-wb="cat"]');
    if (!note || !title) return;
    if (!this.canRemove(note)) return;    // 别人写的，一个字也不替他改
    note.title = title.value.trim();
    note.body = body ? body.value : '';
    note.tags = tag ? parseTags(tag.value) : [];
    if (cat) note.cat = cat.value;
    // 一条字没写的新条目就不留在板上了
    if (note._new && !note.title && !String(note.body || '').trim()) {
      this.state.notes = this.state.notes.filter((n) => n.id !== note.id);
      delete note._new;
      this.draft = null;
      return;
    }
    delete note._new;
    this.save();
  }

  /** 存下并回板上 */
  saveAndBack() {
    const note = this.draft;
    if (!note) return this.back();
    const title = this.q('[data-wb="title"]');
    const empty = !String(title ? title.value : '').trim()
      && !String((this.q('[data-wb="body"]') || {}).value || '').trim();
    this.saveDraft();
    if (empty) {
      this.toast('一条字没写，没给钉上去。', 'warn');
    } else {
      this.toast('记下了，屋里的人都看得见。', 'good');
      this.shareNote(note);      // 写到板上就同步出去
    }
    this.back();
  }

  removeNote() {
    const note = this.draft;
    if (!note) return this.back();
    if (!this.canRemove(note)) return this.toast(`这是${note.by || '旁人'}记的，板上只有本人能揭。`, 'warn');
    if (typeof window !== 'undefined' && window.confirm && !window.confirm('把这一条从板上揭下来？')) return;
    this.shareRemove(note.id);   // 连着 id 也说出去：别人那儿也一并揭下
    this.draft = null;
    this.back();
    this.toast('揭下来了。', 'info');
  }

  back() {
    if (this.draft) this.saveDraft();
    this.draft = null;
    this.showList();
  }

  /* --------------------- 投到电视 / 投到游戏桌 --------------------- */

  /** 一件家具对应一块面板：电视归电视，牌桌归牌桌 */
  stageOf(kind) {
    const isTable = kind === 'gametable';
    return {
      type: isTable ? 'gametable' : 'tv',
      panel: isTable ? this.table : this.tv,
      name: isTable ? '游戏桌' : '电视',
    };
  }

  /** 把这一条里的网址投过去：kind 是 'tv' 或 'gametable'，屋里的人一块儿换 */
  cast(note, kind = 'tv') {
    if (!note) return;
    const n = normalizeUrl((note.body || '').trim() || (note.title || '').trim());
    if (!n) return this.toast('这条里没写着网址，点开在正文里写一个（B 站、视频页都行）。', 'warn');
    const { type, panel, name } = this.stageOf(kind);
    const there = !!panel && !!(this.engine.scene && this.engine.scene.props.some((p) => p.type === type));
    if (!there) return this.toast(`这屋里还没有${name}，先把一件搬进来。`, 'warn');
    panel.change(n.url, { broadcast: true });
    this.toast(`投到${name}去了，屋里的人一起换。`, 'good');
  }

  /* --------------------------- 屋里共用一块板 --------------------------- */

  /** 这块板在哪：手里记着的那块，或是在这屋里找一件 */
  findProp() {
    if (this.prop && this.prop.type === 'whiteboard') return this.prop;
    const p = this.engine.scene ? this.engine.scene.props.find((x) => x.type === 'whiteboard') : null;
    if (p) this.prop = p;
    return p || null;
  }

  /** 揭下来的名单（老存档里没有这个字段，用到了就补上空名单） */
  deadSet() {
    if (!Array.isArray(this.state.dead)) this.state.dead = [];
    return this.state.dead;
  }

  /** 本机那份备份：人走到别处（那张图里没这块板）时先在这儿记账，回屋再并回板上 */
  loadLocal() {
    try {
      const raw = JSON.parse(localStorage.getItem(LOCAL_KEY) || 'null');
      if (raw && Array.isArray(raw.notes)) return raw;
    } catch { /* 没存过或存坏了 */ }
    return null;
  }

  saveLocal() {
    try {
      localStorage.setItem(LOCAL_KEY, JSON.stringify({
        cats: this.state.cats, notes: this.state.notes, dead: this.deadSet(),
      }));
    } catch { /* 写不进去就算了：这块板还是能用，只是换个图会暂时看不见 */ }
  }

  /**
   * 把一份内容（板上留的 / 本机存的 / 网上来的）并进这块板。
   * 同一条按「谁后写的」为准：后改过的才盖得上去，老版本盖不动新的。
   * 揭下来的 id 记在 dead 名单里，下一回再有谁把它翻出来也不往板上贴。
   */
  absorb(src) {
    if (!src) return false;
    const at = (n) => Number(n && n.at) || 0;
    let hit = false;
    for (const id of Array.isArray(src.dead) ? src.dead : []) {
      if (id && !this.deadSet().includes(id)) { this.deadSet().push(id); hit = true; }
    }
    if (Array.isArray(src.dead)) this.state.notes = this.state.notes.filter((n) => !this.deadSet().includes(n.id));
    for (const c of Array.isArray(src.cats) ? src.cats : []) {
      if (c && !this.state.cats.includes(c)) { this.state.cats.push(c); hit = true; }
    }
    for (const note of Array.isArray(src.notes) ? src.notes : []) {
      if (!note || !note.id || this.deadSet().includes(note.id)) continue;
      const i = this.state.notes.findIndex((n) => n.id === note.id);
      if (i >= 0) {
        if (at(note) <= at(this.state.notes[i])) continue;   // 手里的更新，别被旧的盖回去
        this.state.notes[i] = { ...note };
      } else {
        this.state.notes.push({ ...note });
      }
      hit = true;
    }
    if (this.state.notes.length > MAX_NOTES) {
      this.state.notes.sort((a, b) => at(a) - at(b));
      this.state.notes = this.state.notes.slice(this.state.notes.length - MAX_NOTES);
    }
    return hit;
  }

  /** 网上说这一条被揭了：本地也摘掉，正在编辑它的就退回列表 */
  dropIn(id) {
    if (!id) return false;
    if (!this.deadSet().includes(id)) this.deadSet().push(id);
    const before = this.state.notes.length;
    this.state.notes = this.state.notes.filter((n) => n.id !== id);
    if (this.draft && this.draft.id === id) {
      this.draft = null;
      if (this.isOpen()) this.showList();
    }
    return this.state.notes.length !== before;
  }

  /** 落到板上 + 本机备份；面板开着就重画一遍 */
  persist() {
    this.save();
    this.saveLocal();
    if (this.isOpen()) { this.renderTabs(); this.renderTags(); this.renderList(); }
  }

  netSend(data, retain = false) {
    if (this.net && this.net.open) this.net.send('wb', data, retain);
  }

  /** 写完一条：只把这一条发出去（一屋子的人一块儿看见） */
  shareNote(note) {
    if (!note) return;
    this.save();
    this.saveLocal();
    this.netSend({ op: 'note', note: { ...note } });
    this.planSnapshot();
  }

  /** 揭下来一条：连着 id 一起说出去，别人那儿也别再挂着它 */
  shareRemove(id) {
    if (!id) return;
    this.dropIn(id);
    this.save();
    this.saveLocal();
    this.netSend({ op: 'del', id });
    this.planSnapshot();
  }

  /** 整块板发出去：加撤一栏这种改动，一条条讲不如整份讲 */
  shareAll(retain = false) {
    this._lastSnap = Date.now();
    this.netSend({
      op: 'all',
      cats: [...this.state.cats],
      notes: this.state.notes.map((n) => ({ ...n })),
      dead: [...this.deadSet()],
    }, retain);
  }

  /** 开板时问一圈：谁那儿还有更新的，发来对一对 */
  askAll() { this.netSend({ op: 'ask' }); }

  /**
   * 隔一阵把整块板的快照往网上留一份。
   * 保留消息只留最新一条，所以屋里轮着没人在线的时候，后来者也能接着看上一份。
   */
  planSnapshot() {
    if (!this.net || !this.net.open || this._snapTimer) return;
    const wait = Math.max(0, SNAP_GAP - (Date.now() - this._lastSnap));
    this._lastSnap = Date.now() + wait;
    this._snapTimer = setTimeout(() => { this._snapTimer = null; this.shareAll(true); }, wait);
  }

  /** 有人来问（或刚进屋）：把手里这块给他一份。几个人同时答应就错开一下，别都挤着发 */
  answer() {
    if (!this.net || !this.net.open) return;
    const worth = this.state.notes.length > 0 || this.state.cats.some((c) => !BASE_CATS.includes(c));
    if (this._askTimer || !worth) return;
    this._askTimer = setTimeout(() => { this._askTimer = null; this.shareAll(false); }, 200 + Math.random() * 600);
  }

  /** 网上来的消息：动一条就同步一条，问就把整块板甩过去 */
  onNet(msg) {
    if (!msg || !this.net || msg.from === this.net.selfId) return;
    if (msg.t === 'hello') { this.answer(); return; }
    if (msg.t !== 'wb') return;
    if (msg.op === 'ask') { this.answer(); return; }
    if (this._askTimer) { clearTimeout(this._askTimer); this._askTimer = null; }  // 有人替我答了

    if (msg.op === 'del') {
      if (this.dropIn(msg.id)) this.persist();
      return;
    }
    if (msg.op === 'all') {
      if (this.absorb({ cats: msg.cats, notes: msg.notes, dead: msg.dead })) this.persist();
      return;
    }
    if (msg.op === 'note' && msg.note) {
      const fresh = !this.state.notes.some((n) => n.id === msg.note.id);
      if (this.absorb({ cats: msg.note.cat ? [msg.note.cat] : [], notes: [msg.note] })) {
        this.persist();
        this.hintShare(msg.note.by, fresh);
      }
    }
  }

  /** 别人添了一条：板子开着自己看得见，关着时报一声 */
  hintShare(who, fresh) {
    if (!fresh || this.isOpen()) return;
    if (Date.now() - this._lastTip < 3000) return;
    this._lastTip = Date.now();
    this.toast(`${who || '有人'}往板上添了一条，走到板子跟前按 E 看看。`, 'info');
  }

  /* ------------------------------ 落盘 ------------------------------ */

  /** 板上的东西记在这块板自己身上（跟着存档走） */
  save() {
    const prop = this.findProp();
    if (!prop) return;
    prop.state = { ...(prop.state || {}), ...this.state };
    const db = this.engine.db;
    if (db && prop.id) db.table('props').update(prop.id, { state: prop.state });
  }
}
